import { Subject } from 'rxjs';

import { CdkPortalOutlet } from '@angular/cdk/portal';
import { Injector, ViewContainerRef, NgZone } from '@angular/core';

import { GoogleMap } from '@lib/geo/google/map';
import { BASE_COLORS } from '@lib/ui/theme';

import { Evented } from '../core';
import { IGoogleDrawingManagerEvents } from './google-drawing-manager-events';
import { GoogleDrawingManagerOverlays } from './google-drawing-manager-overlays';

export class GoogleDrawingManager extends Evented<IGoogleDrawingManagerEvents> {
  get mapInstance(): google.maps.Map {
    return this._googleMap.mapInstance;
  }
  public readonly overlays: GoogleDrawingManagerOverlays;

  public readonly zone: NgZone;
  public readonly portal: CdkPortalOutlet;
  public readonly injector: Injector;
  public readonly viewContainerRef: ViewContainerRef;

  private readonly _googleMap: GoogleMap;
  private readonly _eventListenners: google.maps.MapsEventListener[] = [];

  private readonly _destroy$: Subject<void> = new Subject();

  private readonly _drawingManager: google.maps.drawing.DrawingManager;

  constructor(googleMap: GoogleMap, options?: google.maps.drawing.DrawingManagerOptions) {
    super();

    this._googleMap = googleMap;
    this._drawingManager = this._createGoogleDrawingManager({
      drawingControl: false,
    });

    if (options) {
      this._drawingManager.setOptions(options);
    }

    this.overlays = new GoogleDrawingManagerOverlays(this._googleMap);

    this._initEventListenners();
  }

  public setOptions(options?: google.maps.drawing.DrawingManagerOptions): void {
    this._drawingManager.setOptions(options);
  }

  public destroy(): void {
    this._destroyEventListenners();

    this._destroy$.next();
    this._destroy$.complete();
  }

  public setDrawingMode(overlayType?: google.maps.drawing.OverlayType): void {
    this._drawingManager.setDrawingMode(overlayType);
  }

  public getDrawingMode(): google.maps.drawing.OverlayType {
    return this._drawingManager.getDrawingMode();
  }

  private _initEventListenners(): void {
    const eventListenners = [
      this._drawingManager.addListener('polygoncomplete', this._onPolygonComplete.bind(this)),
      this._drawingManager.addListener('circlecomplete', this._onCircleComplete.bind(this)),
      this._drawingManager.addListener('rectanglecomplete', this._onRectangleComplete.bind(this)),
      this._drawingManager.addListener('polylinecomplete', this._onPolylineComplete.bind(this)),
      this._drawingManager.addListener('markercomplete', this._onMarkerComplete.bind(this)),
    ];

    this._eventListenners.push(...eventListenners);
  }

  private _destroyEventListenners(): void {
    this._eventListenners.forEach((event) => event.remove());
    this._eventListenners.length = 0;
  }

  private _onPolygonComplete(polygon: google.maps.Polygon): void {
    this.fire('polygoncomplete', polygon);
  }

  private _onCircleComplete(circle: google.maps.Circle): void {
    this.fire('circlecomplete', circle);
  }

  private _onRectangleComplete(rectangle: google.maps.Rectangle): void {
    this.fire('rectanglecomplete', rectangle);
  }

  private _onPolylineComplete(polyline: google.maps.Polyline): void {
    this.fire('polylinecomplete', polyline);
  }

  private _onMarkerComplete(marker: google.maps.Marker): void {
    this.fire('markercomplete', marker);
  }

  private _createGoogleDrawingManager(
    options: google.maps.drawing.DrawingManagerOptions,
  ): google.maps.drawing.DrawingManager {
    const drawingManager = new google.maps.drawing.DrawingManager(options);
    drawingManager.setMap(this._googleMap.mapInstance);
    drawingManager.setOptions({
      polygonOptions: {
        strokeColor: BASE_COLORS['accent-400'],
        strokeOpacity: 1.0,
        strokeWeight: 6,
      },
      circleOptions: {
        strokeColor: BASE_COLORS['accent-400'],
        strokeOpacity: 1.0,
        strokeWeight: 5,
        fillColor: BASE_COLORS['accent-400'],
        fillOpacity: 0.2,
      },
    });
    return drawingManager;
  }
}
